'use client';

import { useMemo } from 'react';

import { useGateValue } from '@statsig/react-bindings';
import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { extractDepositSuggestionsByMethod } from 'components/Deposit/depositBandit';
import { WEB_APPLE_PAY_NON_SAFARI_GATE } from 'constants/depositGates';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError } from 'models';
import type {
    DepositBanditMethodSuggestions,
    DepositBanditPaymentMethod,
    GetPaymentsMetadataResponse,
    PaymentVendorMetadata,
} from 'models/reqres';
import { getPaymentsMetadata } from 'transport/domains/payments';
import { isAndroid, isApplePayEligibleBrowser, isDesktop } from 'utils/device';

// Backend rail vocabulary — mirrors PaymentVendorMetadata.rail values from the
// /payments/metadata response. Single source of truth across the FE.
export type BackendRail = 'debit' | 'ach' | 'crypto' | 'apm' | 'wire';

// ============= VENDOR GATING =============
//
// One source of truth for "which (vendor, rail) tuples exist and how they
// surface in the UI". These are pure functions over a PaymentVendorMetadata[]
// array so they work both from React (React Query data) and from non-hook
// cache reads elsewhere (banks, crypto, BankAccount).

// RAIL_PREFERENCE: ordering used when resolving "which vendor handles this rail
// for this user" — first enabled wins. Multi-vendor rails only.
export const RAIL_PREFERENCE: Record<BackendRail, readonly string[]> = {
    debit: ['stripe', 'checkout'],
    ach: ['plaid', 'aeropay'],
    crypto: ['stripe', 'zerohash', 'coinbase'],
    apm: ['cashapp', 'paypal', 'venmo'],
    wire: ['klear'],
};

// VENDOR_RAIL_METHODS: which UI method tile(s) each (vendor, rail) produces in
// the deposit-method picker. Keyed `${vendor}:${rail}`.
const VENDOR_RAIL_METHODS: Record<string, readonly string[]> = {
    'stripe:debit': ['apple_pay', 'google_pay', 'card'],
    'checkout:debit': ['apple_pay', 'google_pay', 'card'],
    'plaid:ach': ['bank'],
    'aeropay:ach': ['bank'],
    'stripe:crypto': ['crypto'],
    'zerohash:crypto': ['crypto'],
    'coinbase:crypto': ['crypto'],
    'mesh:crypto': ['crypto'],
    'cashapp:apm': ['cashapp'],
    'paypal:apm': ['paypal'],
    'venmo:apm': ['venmo'],
    'klear:wire': ['wire'],
};

// Hardcoded per-platform ordering for the first-deposit method picker only,
// used when the `payment_method_improvements` experiment is enabled.
const DESKTOP_WEB_FIRST_DEPOSIT_ORDER: readonly string[] = [
    'card',
    'google_pay',
    'apple_pay',
    'paypal',
    'venmo',
    'bank',
    'crypto',
    'cashapp',
    'wire',
];

const MOBILE_WEB_FIRST_DEPOSIT_ORDER: readonly string[] = [
    'apple_pay',
    'google_pay',
    'card',
    'venmo',
    'cashapp',
    'paypal',
    'bank',
    'crypto',
    'wire',
];

// Returns the (vendor, rail) metadata entry — exact match required.
export function getVendorMetadata(
    vendors: PaymentVendorMetadata[],
    vendor: string,
    rail: BackendRail,
): PaymentVendorMetadata | undefined {
    return (vendors ?? []).find((v) => v.vendor === vendor && v.rail === rail);
}

// Backend-set disclaimer for a (vendor, rail) entry.
export function getVendorDisclaimer(
    vendors: PaymentVendorMetadata[],
    vendor: string | null | undefined,
    rail: BackendRail,
): string | undefined {
    if (!vendor) return undefined;
    return getVendorMetadata(vendors, vendor, rail)?.disclaimer || undefined;
}

export function isVendorEnabledForRail(
    vendors: PaymentVendorMetadata[],
    vendor: string,
    rail: BackendRail,
    type: 'deposit' | 'withdrawal',
): boolean {
    const metadata = getVendorMetadata(vendors, vendor, rail);
    if (!metadata) return false;
    return type === 'deposit' ? metadata.deposits_enabled : metadata.withdrawals_enabled;
}

// True if any (vendor, *) entry has the requested type enabled.
export function isVendorEnabled(
    vendors: PaymentVendorMetadata[],
    vendor: string,
    type: 'deposit' | 'withdrawal',
): boolean {
    return (vendors ?? []).some(
        (v) => v.vendor === vendor && (type === 'deposit' ? v.deposits_enabled : v.withdrawals_enabled),
    );
}

export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: 'debit',
    type: 'deposit' | 'withdrawal',
): 'stripe' | 'checkout' | null;
export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: 'ach',
    type: 'deposit' | 'withdrawal',
): 'plaid' | 'aeropay' | null;
export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: 'crypto',
    type: 'deposit' | 'withdrawal',
): 'stripe' | 'zerohash' | 'coinbase' | null;
export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: 'apm',
    type: 'deposit' | 'withdrawal',
): 'cashapp' | 'paypal' | 'venmo' | null;
export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: 'wire',
    type: 'deposit' | 'withdrawal',
): 'klear' | null;
export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: BackendRail,
    type: 'deposit' | 'withdrawal',
): string | null;
export function getEnabledVendorForRail(
    vendors: PaymentVendorMetadata[],
    rail: BackendRail,
    type: 'deposit' | 'withdrawal',
): string | null {
    for (const vendor of RAIL_PREFERENCE[rail]) {
        if (isVendorEnabledForRail(vendors, vendor, rail, type)) {
            return vendor;
        }
    }
    return null;
}

// Returns the metadata entry for the first enabled vendor on a rail.
export function getEnabledVendorMetadataForRail(
    vendors: PaymentVendorMetadata[],
    rail: BackendRail,
    type: 'deposit' | 'withdrawal',
): PaymentVendorMetadata | undefined {
    const vendor = getEnabledVendorForRail(vendors, rail, type);
    return vendor ? getVendorMetadata(vendors, vendor, rail) : undefined;
}

export function getOrderedDepositMethods(
    vendors: PaymentVendorMetadata[],
    opts: { applePayNonSafari?: boolean } = {},
): { method: string }[] {
    if (vendors.length === 0) return [];

    const seen = new Set<string>();
    const result: { method: string }[] = [];

    for (const vendor of vendors) {
        if (!vendor.deposits_enabled) continue;
        const methods = VENDOR_RAIL_METHODS[`${vendor.vendor}:${vendor.rail}`];
        if (!methods) continue;

        for (const method of methods) {
            if (seen.has(method)) continue;

            if (method === 'apple_pay') {
                if (!vendor.apple_pay_enabled) continue;
                if (typeof window === 'undefined' || !isApplePayEligibleBrowser(opts.applePayNonSafari ?? false))
                    continue;
            } else if (method === 'google_pay') {
                if (!vendor.google_pay_enabled) continue;
                if (typeof window === 'undefined' || (!isAndroid() && !isDesktop())) continue;
            }

            seen.add(method);
            result.push({ method });
        }
    }

    return result;
}

export function getFirstDepositOrderedMethods(
    vendors: PaymentVendorMetadata[],
    opts: { applePayNonSafari?: boolean } = {},
): { method: string }[] {
    if (vendors.length === 0) return [];

    const available = new Set<string>();
    for (const vendor of vendors) {
        if (!vendor.deposits_enabled) continue;
        const methods = VENDOR_RAIL_METHODS[`${vendor.vendor}:${vendor.rail}`];
        if (!methods) continue;
        for (const method of methods) {
            if (method === 'apple_pay' && !vendor.apple_pay_enabled) continue;
            if (method === 'google_pay' && !vendor.google_pay_enabled) continue;
            available.add(method);
        }
    }

    const order =
        typeof window !== 'undefined' && !isDesktop()
            ? MOBILE_WEB_FIRST_DEPOSIT_ORDER
            : DESKTOP_WEB_FIRST_DEPOSIT_ORDER;

    const result: { method: string }[] = [];
    for (const method of order) {
        if (!available.has(method)) continue;

        if (
            method === 'apple_pay' &&
            (typeof window === 'undefined' || !isApplePayEligibleBrowser(opts.applePayNonSafari ?? false))
        )
            continue;
        if (method === 'google_pay' && (typeof window === 'undefined' || (!isAndroid() && !isDesktop()))) continue;

        result.push({ method });
    }
    return result;
}

// ============= REACT QUERY =============

export const paymentsMetadataKeys = {
    all: ['paymentsMetadata'] as const,
    user: (userId: string | null) => ['paymentsMetadata', userId] as const,
};

export function fetchPaymentsMetadataResponse(userId: string): Promise<GetPaymentsMetadataResponse> {
    return new Promise((resolve, reject) => {
        let settled = false;
        getPaymentsMetadata(
            userId,
            (response: GetPaymentsMetadataResponse) => {
                settled = true;
                resolve({
                    ...response,
                    vendors: Array.isArray(response.vendors) ? response.vendors : [],
                });
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                // The request can finalize without invoking onSuc or onErr — e.g. a
                // forced disconnect makes handleErrorCallback swallow the error. Reject
                // from onFinally so the React Query promise never hangs (a stuck pending
                // query would never refetch/resolve if reattached after re-login). Reject
                // rather than resolve([]) so we don't cache a bogus "no payment methods".
                if (!settled) {
                    settled = true;
                    reject(new Error('payments metadata request did not settle'));
                }
            },
        );
    });
}

// One retry on transient backend errors.
function isRetryableMetadataError(error: ServerError): boolean {
    return error.unknown || error.unavailable || error.internal || error.timeout || error.tooManyRequests;
}

// Shared query options keep hook and imperative consumers in lockstep on key,
// fetch behavior, and retry policy.
export function paymentsMetadataResponseQueryOptions(userId: string) {
    return {
        queryKey: paymentsMetadataKeys.user(userId),
        queryFn: () => fetchPaymentsMetadataResponse(userId),
        retry: (failureCount: number, error: unknown) =>
            failureCount < 1 && isRetryableMetadataError(error as unknown as ServerError),
    };
}

// Imperative cache access for non-hook call sites such as event handlers and
// payment flows that need to force a refresh.
export function invalidatePaymentsMetadata(userId: string): void {
    if (!userId) return;
    void getQueryClient().invalidateQueries({ queryKey: paymentsMetadataKeys.user(userId) });
}

export function ensurePaymentsMetadataResponse(
    userId: string,
    options: { force?: boolean } = {},
): Promise<GetPaymentsMetadataResponse> {
    const queryClient = getQueryClient();
    const queryOptions = paymentsMetadataResponseQueryOptions(userId);
    // fetchQuery reuses fresh data, deduplicates in-flight requests, and awaits a
    // refresh when the aggregate is stale. Derived funding-method queries must not
    // become fresh again from an old aggregate snapshot. force additionally
    // bypasses the default staleTime for payment-routing decisions.
    return options.force
        ? queryClient.fetchQuery({ ...queryOptions, staleTime: 0 })
        : queryClient.fetchQuery(queryOptions);
}

export async function ensurePaymentVendors(
    userId: string,
    options: { force?: boolean } = {},
): Promise<PaymentVendorMetadata[]> {
    return (await ensurePaymentsMetadataResponse(userId, options)).vendors;
}

export function reloadPaymentVendors(userId: string): Promise<PaymentVendorMetadata[]> {
    invalidatePaymentsMetadata(userId);
    return ensurePaymentVendors(userId);
}

export function usePaymentsMetadataResponseQuery({ enabled = true }: { enabled?: boolean } = {}) {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

    return useQuery({
        ...paymentsMetadataResponseQueryOptions(userId ?? ''),
        enabled: enabled && !!userId && isLoggedIn,
    });
}

export function usePaymentsMetadataQuery() {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

    return useQuery({
        ...paymentsMetadataResponseQueryOptions(userId ?? ''),
        enabled: !!userId && isLoggedIn,
        select: (response) => response.vendors,
    });
}

// Convenience hooks over the query for React consumers. Overloads mirror the
// pure getEnabledVendorForRail selector so callers keep the narrow vendor types.
export function useEnabledVendorForRail(rail: 'debit', type: 'deposit' | 'withdrawal'): 'stripe' | 'checkout' | null;
export function useEnabledVendorForRail(rail: 'ach', type: 'deposit' | 'withdrawal'): 'plaid' | 'aeropay' | null;
export function useEnabledVendorForRail(
    rail: 'crypto',
    type: 'deposit' | 'withdrawal',
): 'stripe' | 'zerohash' | 'coinbase' | null;
export function useEnabledVendorForRail(
    rail: 'apm',
    type: 'deposit' | 'withdrawal',
): 'cashapp' | 'paypal' | 'venmo' | null;
export function useEnabledVendorForRail(rail: 'wire', type: 'deposit' | 'withdrawal'): 'klear' | null;
export function useEnabledVendorForRail(rail: BackendRail, type: 'deposit' | 'withdrawal'): string | null;
export function useEnabledVendorForRail(rail: BackendRail, type: 'deposit' | 'withdrawal'): string | null {
    const { data: vendors = [] } = usePaymentsMetadataQuery();
    return getEnabledVendorForRail(vendors, rail, type);
}

export function useVendorMetadata(vendor: string, rail: BackendRail): PaymentVendorMetadata | undefined {
    const { data: vendors = [] } = usePaymentsMetadataQuery();
    return getVendorMetadata(vendors, vendor, rail);
}

export function useEnabledVendorMetadataForRail(
    rail: BackendRail,
    type: 'deposit' | 'withdrawal',
): PaymentVendorMetadata | undefined {
    const { data: vendors = [] } = usePaymentsMetadataQuery();
    return getEnabledVendorMetadataForRail(vendors, rail, type);
}

export function useIsVendorEnabled(vendor: string, type: 'deposit' | 'withdrawal'): boolean {
    const { data: vendors = [] } = usePaymentsMetadataQuery();
    return isVendorEnabled(vendors, vendor, type);
}

export function useOrderedDepositMethods(): { method: string }[] {
    const { data: vendors = [] } = usePaymentsMetadataQuery();
    const applePayNonSafari = useGateValue(WEB_APPLE_PAY_NON_SAFARI_GATE);
    return getOrderedDepositMethods(vendors, { applePayNonSafari });
}

export function useFirstDepositOrderedMethods(): { method: string }[] {
    const { data: vendors = [] } = usePaymentsMetadataQuery();
    const applePayNonSafari = useGateValue(WEB_APPLE_PAY_NON_SAFARI_GATE);
    return getFirstDepositOrderedMethods(vendors, { applePayNonSafari });
}

// True once the metadata query has settled (success or error) — the RQ
// equivalent of the old PaymentsStore.loadedPaymentsMetadata getter.
export function useLoadedPaymentsMetadata(): boolean {
    const { isSuccess, isError } = usePaymentsMetadataQuery();
    return isSuccess || isError;
}

// Deposit-bandit suggestions derived from the metadata vendors. Memoized on the
// vendors reference so the returned object is stable across renders (safe to use
// in effect dependency arrays), matching the old mirrored store field.
export function useDepositSuggestionsByMethod(): Partial<
    Record<DepositBanditPaymentMethod, DepositBanditMethodSuggestions>
> {
    const { data: vendors } = usePaymentsMetadataQuery();
    return useMemo(() => (vendors ? extractDepositSuggestionsByMethod(vendors) : {}), [vendors]);
}

// Query-state-aware resolution of the enabled vendor for a rail, mirroring the
// old PaymentsStore.resolveEnabledVendorForRail (loading / error / ready / none).
export type RailVendorResolution =
    | { state: 'ready'; vendor: string }
    | { state: 'loading' }
    | { state: 'error'; error: ServerError | null }
    | { state: 'none' };

export function useResolveEnabledVendorForRail(
    rail: BackendRail,
    type: 'deposit' | 'withdrawal',
): RailVendorResolution {
    const { data: vendors, isError, error } = usePaymentsMetadataQuery();
    if (isError) {
        return { state: 'error', error: (error as ServerError | null) ?? null };
    }
    if (vendors === undefined) {
        return { state: 'loading' };
    }
    const vendor = getEnabledVendorForRail(vendors, rail, type);
    return vendor ? { state: 'ready', vendor } : { state: 'none' };
}
